import React from 'react';

import repeat from 'lodash/repeat';

import { useTranslate } from '@lumapps/translations';

import { PickerMetaData } from '../types';
import { useFetchMetadataFamilyTree, UseFetchMetadataFamilyTreeProps } from './useFetchMetadataFamilyTree';

export interface UseMetadataPickerProps extends Pick<UseFetchMetadataFamilyTreeProps, 'params' | 'showMultipleLevels'> {
    /** Restrict the selectable values to these exact metadata ids. Undefined means no restriction. */
    allowedMetadataIds?: string[];
}

/**
 * Hook to manage the Metadata Pickers (simple and multiple).
 *
 * The hook will
 * * Fetch the metadata whole family on select open
 * * Manage the search field and choice filtering
 * * Manage choice name formatting with the correct level
 * * Manage action on choice select
 */
export const useMetadataPicker = ({ params, showMultipleLevels, allowedMetadataIds }: UseMetadataPickerProps) => {
    const { translateObject } = useTranslate();
    const [shouldFetch, setFetchEnabled] = React.useState(false);
    const enableFetch = React.useCallback(() => setFetchEnabled(true), []);

    const getMetadataName = React.useCallback(
        (metadata: PickerMetaData) => translateObject(metadata.name),
        [translateObject],
    );
    const [searchText, setSearchText] = React.useState('');

    const { status, metadataList } = useFetchMetadataFamilyTree({
        params,
        shouldFetch,
        showMultipleLevels,
    });

    const allowedMetadataIdSet = React.useMemo(
        () => allowedMetadataIds && new Set(allowedMetadataIds),
        [allowedMetadataIds],
    );

    /** Choices filtered by the current search text */
    const filteredChoices = React.useMemo(
        () =>
            metadataList.filter((metadata: PickerMetaData) => {
                if (metadata.id === params.familyId) {
                    return false;
                }

                if (allowedMetadataIdSet && !allowedMetadataIdSet.has(metadata.id)) {
                    // Match ids exactly. A restriction on a parent does not whitelist its children.
                    return false;
                }

                if (!searchText) {
                    return true;
                }
                const translatedName = translateObject(metadata.name) || metadata.id;

                return translatedName.trim().toLowerCase().includes(searchText.trim().toLowerCase());
            }),
        [allowedMetadataIdSet, params.familyId, metadataList, searchText, translateObject],
    );

    /** Get the metadata value name with level indicator */
    const getValueName = React.useCallback(
        (metadata: PickerMetaData) => {
            const indentation = repeat('— ', metadata.level || 0);
            const metadataName = translateObject(metadata.name);
            return `${indentation}${metadataName}`;
        },
        [translateObject],
    );

    return {
        status,
        filteredChoices,
        searchText,
        getValueName,
        getMetadataName,
        onSearch: setSearchText,
        onOpen: enableFetch,
    };
};
